<template>
  <div class="chat-container">
    <div class="chat-header">
      <div v-if="conversation && conversation.character_details" class="character-info">
        <img :src="conversation.character_details.avatar" :alt="conversation.character_details.name" class="avatar">
        <div class="name">{{ conversation.character_details.name }}</div>
      </div>
      <button class="back-btn" @click="goBack">
        &larr; Back
      </button>
    </div>
    
    <div v-if="loading && !conversation" class="loading">
      Loading conversation...
    </div>
    
    <div v-else-if="error" class="error">
      {{ error }}
    </div>
    
    <div v-else-if="!conversation" class="error">
      Conversation not found
    </div>
    
    <div v-else class="chat-content">
      <div class="messages" ref="messagesContainer">
        <div 
          v-for="(message, index) in messages" 
          :key="message.id"
          :class="['message', message.message_type]"
        >
          <div class="message-content">
            {{ message.content }}
          </div>
          <div class="message-time">
            {{ formatTime(message.created_at) }}
          </div>
        </div>
        
        <div v-if="messages.length === 0" class="empty-chat">
          <p>Start chatting with {{ conversation.character_details.name }}</p>
        </div>
      </div>
      
      <div class="chat-input">
        <textarea 
          v-model="newMessage" 
          placeholder="Type a message..." 
          @keydown.enter.prevent="handleSendMessage"
          :disabled="loading || !hasEnoughTokens"
        ></textarea>
        <button 
          class="send-btn" 
          @click="handleSendMessage" 
          :disabled="!newMessage.trim() || loading || !hasEnoughTokens"
        >
          Send
        </button>
      </div>
      
      <div v-if="!hasEnoughTokens" class="token-warning">
        <p>You don't have free messages left. Subscribe to get more.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'Chat',
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      newMessage: ''
    }
  },
  computed: {
    ...mapGetters('conversations', ['currentConversation', 'messages', 'loading', 'error']),
    ...mapGetters('auth', ['user', 'isUserSubscribed']),
    conversation() {
      return this.currentConversation
    },
    hasEnoughTokens() {
      if (!this.user) return false; // Allow loading state before user is available
      return this.user.free_messages_sent < 5 || this.isUserSubscribed;
    },
  },
  methods: {
    ...mapActions('conversations', ['fetchConversation', 'sendMessage']),
    ...mapActions('auth', ['checkSubscriptionStatus']),
    goBack() {
      this.$router.push({ name: 'Messages' })
    },
    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    async handleSendMessage() {
      if (!this.newMessage.trim() || this.loading || !this.hasEnoughTokens) return
      
      const message = this.newMessage
      this.newMessage = ''
      
      try {
        await this.sendMessage(message)
        this.scrollToBottom()
      } catch (error) {
        console.error('Error sending message:', error)
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          const container = this.$refs.messagesContainer;
          // Use smooth scrolling for better UX
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
          
          // Add a small delay to ensure scrolling works even when keyboard appears
          setTimeout(() => {
            container.scrollTop = container.scrollHeight;
          }, 100);
        }
      })
    }
  },
  async created() {
    await this.fetchConversation(this.id)
  },
  mounted() {
    this.scrollToBottom()
  },
  updated() {
    this.scrollToBottom()
  }
}
</script>

<style scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-width: 800px;
  margin: 0 auto;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: var(--tg-theme-bg-color);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 10;
}

.character-info {
  display: flex;
  align-items: center;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
}

.name {
  font-weight: bold;
}

.back-btn {
  background: none;
  border: none;
  color: var(--tg-theme-link-color);
  cursor: pointer;
  padding: 8px;
}

.chat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: env(safe-area-inset-bottom, 0);
}

.loading, .error {
  text-align: center;
  padding: 20px;
  color: var(--tg-theme-hint-color);
}

.error {
  color: #e53935;
}

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-chat {
  text-align: center;
  color: var(--tg-theme-hint-color);
  margin: auto 0;
  padding: 20px;
}

.message {
  max-width: 80%;
  padding: 12px;
  border-radius: 12px;
  position: relative;
}

.message.user {
  align-self: flex-end;
  background-color: var(--tg-theme-button-color);
  color: var(--tg-theme-button-text-color);
}

.message.character {
  align-self: flex-start;
  background-color: rgba(0, 0, 0, 0.05);
}

.message.system {
  align-self: center;
  background-color: rgba(0, 0, 0, 0.03);
  color: var(--tg-theme-hint-color);
  font-style: italic;
  max-width: 90%;
}

.message-content {
  word-break: break-word;
}

.message-time {
  font-size: 12px;
  opacity: 0.7;
  text-align: right;
  margin-top: 4px;
}

.chat-input {
  display: flex;
  padding: 12px 16px;
  background-color: var(--tg-theme-bg-color);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  position: sticky;
  bottom: 0;
  z-index: 10;
  width: 100%;
  box-sizing: border-box;
}

textarea {
  flex: 1;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  padding: 10px 16px;
  resize: none;
  height: 40px;
  max-height: 120px;
  background-color: var(--tg-theme-bg-color);
  color: var(--tg-theme-text-color);
}

textarea:focus {
  outline: none;
  border-color: var(--tg-theme-button-color);
}

.send-btn {
  background-color: var(--tg-theme-button-color);
  color: var(--tg-theme-button-text-color);
  border: none;
  border-radius: 20px;
  padding: 0 16px;
  margin-left: 8px;
  cursor: pointer;
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.token-warning {
  background-color: rgba(229, 57, 53, 0.1);
  color: #e53935;
  padding: 12px;
  text-align: center;
  margin: 0 16px 16px;
  border-radius: 8px;
  position: relative;
  z-index: 9;
}
</style> 